import { BGG_MARKETPLACE_URL } from '../constants';

/**
 * Price History Processor
 * 
 * Responsible for processing and analyzing price history data.
 * Contains statistical methods for outlier detection and price averaging.
 */

/**
 * Calculate Z-Scores for an array of numbers
 * Used for statistical outlier detection
 * 
 * @param {number[]} numbers - Array of numeric values
 * @returns {number[]} Array of z-scores corresponding to each input value
 */
export const calculateZScore = (numbers) => {
    if (!numbers || numbers.length === 0) {
        return [];
    }
    
    const mean = numbers.reduce((acc, val) => acc + val, 0) / numbers.length;
    const variance = numbers.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / numbers.length;
    const standardDeviation = Math.sqrt(variance);
    
    // Handle case where standard deviation is 0 (all values are the same)
    if (standardDeviation === 0) {
        return numbers.map(() => 0);
    }
    
    return numbers.map(num => (num - mean) / standardDeviation);
};

/**
 * Price history analysis result
 */
class PriceHistoryAnalysis {
    constructor(core, outliers, averagePrice) {
        this.core = core;
        this.outliers = outliers;
        this.averagePrice = averagePrice;
    }
}

/**
 * Analyze price history data and calculate statistics
 * Separates core prices from outliers using z-score method
 * 
 * @param {Array} items - Array of price history items from API
 * @param {number} zThreshold - Z-score threshold for outlier detection (default: 2)
 * @returns {PriceHistoryAnalysis} Analysis result with core prices, outliers, and average
 */
export const analyzePriceHistory = (items, zThreshold = 2) => {
    if (!items || items.length === 0) {
        return new PriceHistoryAnalysis([], [], 0);
    }
    
    const priceArray = items.map(item => parseFloat(item.price));
    const zScores = calculateZScore(priceArray);
    
    const outliers = priceArray.filter((_, index) => {
        return zScores[index] > zThreshold || zScores[index] < -zThreshold;
    });
    
    const core = priceArray.filter((_, index) => {
        return zScores[index] > -zThreshold && zScores[index] < zThreshold;
    });
    
    if (core.length < 1) {
        return new PriceHistoryAnalysis([], outliers, 0);
    }
    
    const pricesSum = core.reduce((acc, val) => acc + val, 0);
    let average = pricesSum / core.length;
    average = average % 1 === 0 ? average : parseFloat(average.toFixed(2));
    
    return new PriceHistoryAnalysis(core, outliers, average);
};

/**
 * Format a price for display
 * 
 * @param {number} price - Price value to format
 * @returns {string} Formatted price string
 */
export const formatPrice = (price) => {
    if (price === null || price === undefined) return '0.00';
    const numericPrice = parseFloat(price);
    if (isNaN(numericPrice)) return '0.00';
    return numericPrice.toFixed(2);
};

/**
 * Generate a formatted comment string for price history display
 * 
 * @param {PriceHistoryAnalysis} analysis - Price history analysis result
 * @param {string} objectId - BGG object ID for the game
 * @returns {string} HTML-formatted comment string
 */
export const formatPriceHistoryComment = (analysis, objectId = null) => {
    if (!analysis || analysis.core.length === 0) {
        return 'No relevant marketplace sales found.';
    }
    
    let comment = `BGG marketplace average price for the last ${analysis.core.length} sales was $${formatPrice(analysis.averagePrice)}.`;
    
    if (analysis.outliers.length > 0) {
        const wasWere = analysis.outliers.length > 1 ? 'were' : 'was';
        const outlierLabel = analysis.outliers.length > 1 ? 'outliers' : 'an outlier';
        const outlierPrices = analysis.outliers.map(o => `$${formatPrice(o)}`).join(', ');
        comment += ` ${analysis.outliers.length} ${wasWere} ignored as ${outlierLabel}: ${outlierPrices}.`;
    }
    
    if (objectId) {
        comment += ` </br><a href="${BGG_MARKETPLACE_URL}/${objectId}" target="_blank" rel="noreferrer">See price history</a>`;
    }
    
    return comment;
};

const priceHistoryProcessor = {
    calculateZScore,
    analyzePriceHistory,
    formatPrice,
    formatPriceHistoryComment
};

export default priceHistoryProcessor;
